import { SupabaseClient } from '@supabase/supabase-js';
import camelcaseKeys from 'camelcase-keys';

import { Axis, Database, withLogs } from '@core';

import { mapBaseUiNode, withLimit, type BaseNode } from './uiNodes';

const UI_GRAPH_LINES_QUERY = `
    *,
    ui_graph_lines(*, time_series_measurements(time_series_types(units(*))))
` as const;

export type GraphLineNode = BaseNode & {
    axis: Axis | null;
    categories: GraphLineCategories;
    timeSeries: GraphLineTimeSeries;
};

type GraphLineCategories = {
    metric: string;
};

type GraphLineTimeSeries = {
    id: string;
    unit: string;
};

export const fetchUiGraphLines = withLogs(async (supabase: SupabaseClient<Database>): Promise<GraphLineNode[]> => {
    const { data, error } = await withLimit(
        3000,
        supabase
            .from('ui_nodes')
            .select(UI_GRAPH_LINES_QUERY)
            .eq('type', 'graph_line')
            .order('id', { ascending: true }),
    );

    if (error) {
        console.error('error fetching ui_graph_lines:', error);
        return [];
    }
    const uiGraphLines = camelcaseKeys(data, { deep: true });
    return uiGraphLines.flatMap((uiNode) => {
        const graphLine = uiNode.uiGraphLines;
        if (!graphLine || !graphLine.timeSeriesMeasurements) return [];

        const categories = { metric: graphLine.metric };
        const timeSeries = {
            id: graphLine.timeSeriesMeasurementName,
            unit: graphLine.timeSeriesMeasurements.timeSeriesTypes.units.notation,
        };

        return [{ ...mapBaseUiNode(uiNode), categories, timeSeries, axis: graphLine.axis }];
    });
}, 'fetchUiGraphLines');
